interface DataPoint {
  label: string
  value: number
  value2?: number
}

interface LineChartProps {
  points: DataPoint[]
  width?: number
  height?: number
  color?: string
  color2?: string
  formatValue?: (v: number) => string
  showGrid?: boolean
}

export default function LineChart({
  points,
  width = 320,
  height = 120,
  color = '#22d3ee',
  color2 = '#a78bfa',
  formatValue,
  showGrid = true,
}: LineChartProps) {
  if (points.length < 2) return null

  const padL = 8, padR = 8, padT = 8, padB = 24
  const w = width - padL - padR
  const h = height - padT - padB

  const allValues = points.flatMap(p => p.value2 != null ? [p.value, p.value2] : [p.value])
  const min = Math.min(...allValues)
  const max = Math.max(...allValues, min + 1)
  const range = max - min

  function toX(i: number) { return padL + (i / (points.length - 1)) * w }
  function toY(v: number) { return padT + h - ((v - min) / range) * h }

  function line(key: 'value' | 'value2') {
    return points
      .filter(p => key === 'value' || p.value2 != null)
      .map((p, i) => {
        const v = key === 'value' ? p.value : (p.value2 ?? 0)
        return `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`
      })
      .join(' ')
  }

  const gridRows = 3
  const gridVals = Array.from({ length: gridRows + 1 }, (_, i) => min + (range / gridRows) * i)

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible w-full">
      {showGrid && gridVals.map((v, i) => {
        const y = toY(v)
        return (
          <g key={i}>
            <line x1={padL} x2={padL + w} y1={y} y2={y} className="stroke-line" strokeWidth={1} strokeDasharray="3,3" />
            <text x={padL - 2} y={y + 4} fontSize={9} className="fill-ink-muted" textAnchor="end">
              {formatValue ? formatValue(v) : Math.round(v)}
            </text>
          </g>
        )
      })}

      <path d={line('value')} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {points.some(p => p.value2 != null) && (
        <path d={line('value2')} fill="none" stroke={color2} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      )}

      {points.map((p, i) => (
        <text key={i} x={toX(i)} y={height - 6} fontSize={9} className="fill-ink-muted" textAnchor="middle">
          {p.label}
        </text>
      ))}

      {points.map((p, i) => (
        <circle key={i} cx={toX(i)} cy={toY(p.value)} r={2.5} fill={color} />
      ))}
    </svg>
  )
}
